// src/pages/AdvertisementStart.tsx
import React, { useState, useEffect, } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarAds from './Sidebars/SidebarAds'; // 🔧 adjust path if needed
import { useTranslation } from 'react-i18next';
import api from '../../shared/api/axios'; // 🔧 later: use this to load real data
import { useAuth } from '../../shared/contexts/AuthContext';

const GOLD = '#D4AF37';
const BG = '#050912';
const CARD_BG = '#0D101B';
const BORDER = '#22283a';

type CampaignSummary = {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'finished';
  spendTotal: number;
  dailyBudget: number;
  impressions: number;
  clicks: number;
  startDate?: string | null;
  endDate?: string | null;
  location?: string | null;
};
type ApiCampaign = {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'finished';
  dailyBudget?: number;
  startDate?: string | null;
  endDate?: string | null;
  targeting?: {
    countries?: string[];
    continents?: string[];
    cities?: string[];
  };
  stats?: {
    impressionsTotal?: number;
    clicksTotal?: number;
    spendToday?: number;
    spendTotal?: number;
  };
};


const AdvertisementStart: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
const { user } = useAuth();
const advertiser = user?.advertiserProfile;

  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
const [showInactiveModal, setShowInactiveModal] = useState(false);

  const totalSpend = campaigns.reduce((sum, c) => sum + c.spendTotal, 0);
  const totalImpressions = campaigns.reduce((sum, c) => sum + c.impressions, 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0);
  
  const totalCTR =
    totalImpressions > 0
      ? (totalClicks / totalImpressions) * 100
      : 0;
  
   
  const isAdAccountActive =
  !!advertiser &&
  advertiser.completed === true &&
  advertiser.approved === true &&
  advertiser.suspended !== true;


 const handleCreateCampaign = () => {
  if (!isAdAccountActive) {
    setShowInactiveModal(true);
    return;
  }

  navigate("/adupload", { state: { reset: true } });
};


  const handleViewCampaign = (id: string) => {
    navigate(`/advertisement/campaign/${id}`);
  };

  const patchCampaignStatus = async (id: string, status: 'running' | 'paused' | 'finished') => {
    try {
      await api.patch(`/ads/${id}`, { status });
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, status } : c
        )
      );
    } catch (err) {
      console.error('[AdvertisementStart] Failed to update campaign status', err);
      alert('Failed to update campaign status. Please try again.');
    }
  };

  const handlePauseCampaign = (id: string) => {
    patchCampaignStatus(id, 'paused');
  };

  const handleResumeCampaign = (id: string) => {
    patchCampaignStatus(id, 'running');
  };

  const handleEndCampaign = (id: string) => {
    if (!window.confirm('End this campaign? It will stop delivering ads.')) return;
    patchCampaignStatus(id, 'finished');
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(); // or pass locale if you want
  };

//Wire to GET /ads/mine
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get('/ads/mine');
        const apiCampaigns: ApiCampaign[] = res.data?.campaigns || [];
        if (cancelled) return;
        
        const formatTargeting = (t?: ApiCampaign["targeting"]) => {
          const parts = [
            ...(t?.countries ?? []),
            ...(t?.continents ?? []),
            ...(t?.cities ?? []),
          ];
          return parts.length ? parts.join(", ") : "—";
        };

        const mapped: CampaignSummary[] = apiCampaigns.map((c) => ({
          id: c.id,
          name: c.name,
          status: c.status,
          spendTotal: c.stats?.spendTotal ?? 0,
          dailyBudget: c.dailyBudget ?? 0,
          impressions: c.stats?.impressionsTotal ?? 0,
          clicks: c.stats?.clicksTotal ?? 0,
          startDate: c.startDate ?? null,
          endDate: c.endDate ?? null,
          location: formatTargeting(c.targeting), 
        }));
        
        setCampaigns(mapped);
      } catch (err) {
        console.error('[AdvertisementStart] Failed to load campaigns', err);
        if (!cancelled) {
          setError('Failed to load campaigns');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: BG,
        color: '#eee',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Sidebar */}
      <SidebarAds />

      {/* Main content */}
      <main
        style={{
          marginLeft: 200, // match sidebar width
          padding: '24px 32px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              🛍️ {t('ad.start.title')}
            </h1>
            <p
              style={{
                marginTop: 6,
                marginBottom: 0,
                color: '#9ca3af',
                fontSize: 14,
                maxWidth: 520,
              }}
            >
              {t(
                'ad.start.subtitle',
                'Promote your posts as commercial videos, track billing, and see how your campaigns perform.'
              )}
            </p>
          </div>

          <button
            onClick={handleCreateCampaign}
            style={{
              padding: '10px 18px',
              borderRadius: 999,
              border: `1px solid ${GOLD}`,
              background:
                'radial-gradient(circle at top left, #fceabb 0%, #f8b500 45%, #b88700 100%)',
              color: '#111',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              boxShadow: '0 0 18px rgba(212, 175, 55, 0.35)',
            }}
          >
            ➕ {t('ad.start.buttons.newCampaign', 'Create new campaign')}
          </button>
        </header>

        {/* Top row: billing summary + account status / steps */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr)',
            gap: 20,
            marginBottom: 24,
          }}
        >
          {/* Billing summary */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 20px 35px rgba(0,0,0,0.35)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                {t('ad.start.billingTitle', 'Billing overview')}
              </h2>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>

              <button
                style={{
                  borderRadius: 999,
                  border: `1px solid ${BORDER}`,
                  background: 'transparent',
                  color: '#e5e7eb',
                  fontSize: 12,
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/managebillings')}
              >
                {t('ad.start.billing.managePayment', 'Manage Billings')}
              </button>
     <span
  style={{
    fontSize: 11,
    padding: '4px 8px',
    borderRadius: 999,
    border: `1px solid ${isAdAccountActive ? GOLD : '#f87171'}`,
    color: isAdAccountActive ? GOLD : '#f87171',
    textTransform: 'uppercase',
    letterSpacing: 0.07,
  }}
>
  {isAdAccountActive ? 'Ad account active' : 'Ad account not active'}
</span>


              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 16,
                marginTop: 8,
              }}
            >
              {/* Spend total */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#9ca3af',
                    marginBottom: 4,
                  }}
                >
                  {t('ad.start.billing.totalSpend', 'Total spend')}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    color: '#f9fafb',
                  }}
                >
                  €{totalSpend.toFixed(2)}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: '#6b7280',
                    marginTop: 2,
                  }}
                >
                  {t('ad.start.billing.totalSpendHint', 'Total amount spent across your campaigns.')}

                </div>
                
              </div>


       {/* CTR */}
<div>
  <div
    style={{
      fontSize: 12,
      color: '#9ca3af',
      marginBottom: 4,
    }}
  >
    {t('ad.start.billing.ctr', 'CTR')}
  </div>
  <div
    style={{
      fontSize: 22,
      fontWeight: 600,
      color: "#ccc",
       
    }}
    title={`${totalClicks} clicks / ${totalImpressions} impressions`}
  >
    {totalCTR.toFixed(2)}%
  </div>
  <div
    style={{
      fontSize: 11,
      color: '#6b7280',
      marginTop: 2,
    }}
  >
    {t('ad.start.billing.ctrHint', 'Click-through rate across all campaigns.')}
  </div>
</div>
            </div>

            {/* Payment method stub */}
            <div
              style={{
                marginTop: 18,
                paddingTop: 10,
                borderTop: `1px solid ${BORDER}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#9ca3af',
                    marginBottom: 2,
                  }}
                >
                  {t('ad.start.billing.paymentMethod', 'Payment method')}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: '#e5e7eb',
                  }}
                >
                  •••• 1234 · VISA
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: '#6b7280',
                    marginTop: 2,
                  }}
                >
                  {t('ad.start.billing.paymentHint', '')}
                </div>
              </div>

              <button
                style={{
                  borderRadius: 999,
                  border: `1px solid ${BORDER}`,
                  background: 'transparent',
                  color: '#e5e7eb',
                  fontSize: 12,
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/changepayment')}
              >
                {t('ad.start.billing.changePayment', 'Change Payment method')}
              </button>    
              

              
            </div>
          </div>

          {/* Getting started / steps */}
          <div
            style={{
              background: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 16,
              padding: 18,
              boxShadow: '0 16px 30px rgba(0,0,0,0.3)',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              {t('ad.start.steps.title', 'How advertising works')}
            </h2>

            <ol
              style={{
                margin: 0,
                paddingLeft: 18,
                fontSize: 13,
                color: '#d1d5db',
                lineHeight: 1.5,
              }}
            >
              <li style={{ marginBottom: 6 }}>
                {t(
                  'ad.start.steps.step1',
                  'Choose an existing post to promote or upload a new commercial video.'
                )}
              </li>
              <li style={{ marginBottom: 6 }}>
                {t(
                  'ad.start.steps.step2',
                  'Set your daily budget and target countries/cities where you want to show ads.'
                )}
              </li>
              <li style={{ marginBottom: 6 }}>
                {t(
                  'ad.start.steps.step3',
                  'Add a link and call-to-action so people can visit your website or profile.'
                )}
              </li>
              <li>
                {t(
                  'ad.start.steps.step4',
                  'Review performance here and pause or edit campaigns any time.'
                )}
              </li>
            </ol>
          </div>
        </section>

        {/* Campaign overview */}
        <section
          style={{
            background: CARD_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: 18,
            boxShadow: '0 16px 30px rgba(0,0,0,0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {t('ad.start.campaigns.title', 'Your campaigns')}
            </h2>
            <span
              style={{
                fontSize: 12,
                color: '#9ca3af',
              }}
            >
              {campaigns.length === 0
                ? t('ad.start.campaigns.none', 'No campaigns yet')
                : t('ad.start.campaigns.count', '{{count}} active / draft', {
                    count: campaigns.length,
                  })}
            </span>
          </div>

          {campaigns.length === 0 ? (
            <div
              style={{
                padding: 16,
                fontSize: 13,
                color: '#9ca3af',
              }}
            >
              {t(
                'ad.start.campaigns.emptyHint',
                'Once you create your first advertising post, it will appear here with billing and performance details.'
              )}
            </div>
          ) : (
            <div
              style={{
                overflowX: 'auto',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: `1px solid ${BORDER}`,
                    }}
                  >
                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.start.campaigns.columns.name', 'Campaign')}
                    </th>
                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.start.campaigns.columns.status', 'Status')}
                    </th>
                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.start.campaigns.columns.spendTotal', 'Total Spend')}
                    </th>
                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                       {t('ad.start.campaigns.columns.dailyLimit', 'Daily limit')}
                    </th>
                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.start.campaigns.columns.startDate', 'Start')}
                    </th>
                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.start.campaigns.columns.endDate', 'End')}
                    </th>

                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                    <th>{t('ad.start.campaigns.columns.ctr', 'CTR')}</th>
                    </th>
                    
                    <th style={{ padding: '8px 6px', fontWeight: 500 }}>
                      {t('ad.start.campaigns.columns.location', 'Location')}
                    </th>
                    <th style={{ padding: '8px 6px', fontWeight: 500 }} />
                  </tr>
                </thead>
                <tbody>
  {campaigns.map((c) => (
    <tr
      key={c.id}
      style={{
        borderBottom: `1px solid rgba(31,41,55,0.7)`,
      }}
    >
      {/* Campaign name → clickable, same as "View" */}
      <td style={{ padding: '8px 6px' }}>
        <button
          type="button"
          onClick={() => handleViewCampaign(c.id)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            color: '#e5e7eb',
            cursor: 'pointer',
            fontSize: 13,
            textAlign: 'left',
            textDecoration: 'none',
            textDecorationThickness: '1px',
            textUnderlineOffset: 3,
          }}
        >
          {c.name}
        </button>
      </td>

      <td style={{ padding: '8px 6px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 11,
            background:
              c.status === 'running'
                ? 'rgba(34,197,94,0.16)'
                : c.status === 'paused'
                ? 'rgba(234,179,8,0.16)'
                : 'rgba(148,163,184,0.16)',
            color:
              c.status === 'running'
                ? '#4ade80'
                : c.status === 'paused'
                ? '#facc15'
                : '#e5e7eb',
          }}
        >
          {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
        </span>
      </td>
      <td style={{ padding: '8px 6px' }}>
         €{c.spendTotal.toFixed(2)}
      </td>
      <td style={{ padding: '8px 6px' }}>
         €{(c.dailyBudget ?? 0).toFixed(2)}
      </td>
      <td style={{ padding: '8px 6px', color: '#d1d5db' }}>
  {formatDate(c.startDate)}
</td>
<td style={{ padding: '8px 6px', color: '#d1d5db' }}>
  {formatDate(c.endDate)}
</td>
<td style={{ padding: '8px 6px' }}>
  {(() => {
    const ctr =
      c.impressions > 0
        ? (c.clicks / c.impressions) * 100
        : 0;


    return (
      <span
        style={{ color: "#ccc", fontWeight: 600 }}
        title={`${c.clicks} clicks / ${c.impressions} impressions`}
      >
        {ctr.toFixed(2)}%
      </span>
    );
  })()}
</td>

      <td style={{ padding: '8px 6px', color: '#d1d5db' }}>
        {c.location ?? '—'}
      </td>

      {/* Actions */}
      <td style={{ padding: '8px 6px', textAlign: 'right' }}>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          {/* Pause / Resume depending on status */}
          {c.status === 'running' ? (
            <button
              onClick={() => handlePauseCampaign(c.id)}
              style={{
                borderRadius: 999,
                border: `1px solid ${BORDER}`,
                background: 'transparent',
                color: '#f97316',
                fontSize: 12,
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            >
              {t('ad.start.campaigns.pause', 'Pause')}
            </button>
          ) : c.status === 'paused' ? (
            <button
              onClick={() => handleResumeCampaign(c.id)}
              style={{
                borderRadius: 999,
                border: `1px solid ${BORDER}`,
                background: 'transparent',
                color: '#4ade80',
                fontSize: 12,
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            >
              {t('ad.start.campaigns.resume', 'Resume')}
            </button>
          ) : null}

          {/* End / finish */}
          {c.status !== 'finished' && (
            <button
              onClick={() => handleEndCampaign(c.id)}
              style={{
                borderRadius: 999,
                border: `1px solid ${BORDER}`,
                background: 'transparent',
                color: '#f87171',
                fontSize: 12,
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            >
              {t('ad.start.campaigns.end', 'End')}
            </button>
          )}

          {/* View detail */}
          <button
            onClick={() => handleViewCampaign(c.id)}
            style={{
              borderRadius: 999,
              border: `1px solid ${BORDER}`,
              background: 'transparent',
              color: '#e5e7eb',
              fontSize: 12,
              padding: '5px 10px',
              cursor: 'pointer',
            }}
          >
            {t('ad.start.campaigns.view', 'View')}
          </button>
        </div>
      </td>
    </tr>
  ))}
</tbody>

              </table>
            </div>
          )}
        </section>
        {showInactiveModal && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
    }}
  >
    <div
      style={{
        background: "#0D101B",
        border: "1px solid #22283a",
        borderRadius: 16,
        padding: 24,
        width: "100%",
        maxWidth: 420,
        boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>
        Advertiser account not active
      </h3>

      <p style={{ fontSize: 14, color: "#9ca3af", lineHeight: 1.5 }}>
        Your advertiser account is not active yet.  
        You must complete advertiser onboarding and billing before you can
        publish ads.
      </p>

      <div
        style={{
          marginTop: 20,
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <button
          onClick={() => setShowInactiveModal(false)}
          style={{
            padding: "10px 16px",
            borderRadius: 999,
            border: "1px solid #4b5563",
            background: "transparent",
            color: "#e5e7eb",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>

        <button
          onClick={() => {
            setShowInactiveModal(false);
            navigate("/adupload");
          }}
          style={{
            padding: "10px 18px",
            borderRadius: 999,
            background: "#22c55e",
            color: "#020617",
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
          }}
        >
          Continue
        </button>
      </div>
    </div>
  </div>
)}

      </main>
    </div>
  );
};

export default AdvertisementStart;
